// Componentes do sistema Legal/Consentimento (LGPD), compartilhados entre os
// painéis. Montados globalmente no shell (apps/web). [[project-legal-consent-system]]
//
//   <CookieBanner/>  — banner de cookies (logado ou anônimo). Geo coarse só
//                      (sem prompt de GPS aqui — seria agressivo no 1º load).
//   <LegalGate/>     — modal BLOQUEANTE de aceite de Termos + Privacidade.
//                      Self-fetch em /api/legal/pending. Captura GPS opcional
//                      no clique de aceitar (o usuário pode negar).
//   <LegalDocViewer/>— renderiza o markdown de um documento legal.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { X } from "lucide-react";
import { Switch } from "./ui/switch";

// ============================================================================
// Markdown → HTML (renderer enxuto e próprio pros documentos legais: headings,
// listas, tabelas, blockquote, hr, negrito/itálico/links). Escapa HTML antes
// de formatar pra evitar injeção.
// ============================================================================

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inline(s: string): string {
  let out = esc(s);
  // links [texto](url) — só http(s) e #
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+|#)\)/g,
    (_m, t, u) =>
      `<a href="${u}" target="_blank" rel="noopener noreferrer" class="text-violet-600 underline dark:text-violet-400">${t}</a>`
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/`([^`]+)`/g, '<code class="rounded bg-muted px-1 py-0.5 text-[0.85em]">$1</code>');
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  return out;
}

export function legalMarkdownToHtml(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const html: string[] = [];
  let i = 0;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      html.push(`<p class="my-3 leading-relaxed">${inline(para.join(" "))}</p>`);
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") {
      flushPara();
      i++;
      continue;
    }
    // headings
    const h = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (h) {
      flushPara();
      const level = h[1].length;
      const cls =
        level === 1
          ? "mt-2 mb-4 text-xl font-bold text-violet-700 dark:text-violet-300"
          : level === 2
            ? "mt-6 mb-2 text-base font-bold text-violet-700 dark:text-violet-300"
            : "mt-4 mb-1 text-sm font-semibold text-foreground";
      html.push(`<h${level} class="${cls}">${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }
    // hr
    if (/^---+$/.test(trimmed)) {
      flushPara();
      html.push('<hr class="my-4 border-border"/>');
      i++;
      continue;
    }
    // blockquote (agrupa)
    if (trimmed.startsWith(">")) {
      flushPara();
      const buf: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        buf.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      html.push(
        `<blockquote class="my-3 border-l-2 border-violet-400 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">${inline(
          buf.join(" ")
        )}</blockquote>`
      );
      continue;
    }
    // tabela (linhas começando com |)
    if (trimmed.startsWith("|")) {
      flushPara();
      const rows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].trim());
        i++;
      }
      const parseRow = (r: string) =>
        r
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => c.trim());
      const header = parseRow(rows[0]);
      const bodyRows = rows.slice(2); // pula separador ---
      const thead =
        "<thead><tr>" +
        header
          .map(
            (c) =>
              `<th class="border border-border bg-muted px-2 py-1 text-left text-xs font-semibold">${inline(
                c
              )}</th>`
          )
          .join("") +
        "</tr></thead>";
      const tbody =
        "<tbody>" +
        bodyRows
          .map(
            (r) =>
              "<tr>" +
              parseRow(r)
                .map(
                  (c) =>
                    `<td class="border border-border px-2 py-1 align-top text-xs">${inline(
                      c
                    )}</td>`
                )
                .join("") +
              "</tr>"
          )
          .join("") +
        "</tbody>";
      html.push(
        `<div class="my-3 overflow-x-auto"><table class="w-full border-collapse text-sm">${thead}${tbody}</table></div>`
      );
      continue;
    }
    // listas (- ou número.) — agrupa consecutivas
    if (/^(-|\d+\.)\s+/.test(trimmed)) {
      flushPara();
      const ordered = /^\d+\.\s+/.test(trimmed);
      const items: string[] = [];
      while (i < lines.length && /^(-|\d+\.)\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^(-|\d+\.)\s+/, ""));
        i++;
      }
      const tag = ordered ? "ol" : "ul";
      const listCls = ordered
        ? "my-3 list-decimal space-y-1 pl-5 text-sm"
        : "my-3 list-disc space-y-1 pl-5 text-sm";
      html.push(
        `<${tag} class="${listCls}">${items
          .map((it) => `<li class="leading-relaxed">${inline(it)}</li>`)
          .join("")}</${tag}>`
      );
      continue;
    }
    // parágrafo
    para.push(trimmed);
    i++;
  }
  flushPara();
  return html.join("\n");
}

export function LegalDocViewer({
  markdown,
  class: className
}: {
  markdown: string;
  class?: string;
}) {
  const html = useMemo(() => legalMarkdownToHtml(markdown), [markdown]);
  return (
    <div
      class={"text-sm text-foreground " + (className ?? "")}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ============================================================================
// Helpers
// ============================================================================

interface GpsCoords {
  lat: number;
  lng: number;
  accuracy: number;
}

/** Tenta GPS preciso (com timeout). Resolve null se negado/indisponível —
 *  nunca rejeita, pra não bloquear o fluxo de aceite. */
function tryGps(): Promise<GpsCoords | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy
        }),
      () => resolve(null),
      { timeout: 8000, maximumAge: 600000, enableHighAccuracy: false }
    );
  });
}

// ============================================================================
// Cookie banner
// ============================================================================

const COOKIE_KEY = "legal:cookie-consent:v1";

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [preferences, setPreferences] = useState(true);

  useEffect(() => {
    // Já escolheu antes? Não mostra.
    try {
      if (localStorage.getItem(COOKIE_KEY)) return;
    } catch {
      /* localStorage indisponível — segue e deixa o servidor decidir */
    }
    // O servidor decide se o banner aparece pra este visitante (respeita o
    // rollout: em 'allowlist' só a lista vê; em 'all' todos veem).
    fetch("/api/legal/pending", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.cookieBanner) setVisible(true);
      })
      .catch(() => {
        /* offline — não mostra */
      });
  }, []);

  async function save(choices: Record<string, boolean>) {
    try {
      localStorage.setItem(
        COOKIE_KEY,
        JSON.stringify({ choices, at: Date.now() })
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
    // Best-effort: registra no ledger (não bloqueia, não trava o app se falhar).
    try {
      await fetch("/api/legal/cookie-consent", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ choices })
      });
    } catch {
      /* ignore */
    }
  }

  if (!visible) return null;

  return (
    <div
      style={{
        zIndex: 10058,
        left: "50%",
        transform: "translateX(-50%)",
        width: "calc(100% - 1.5rem)",
        maxWidth: "30rem"
      }}
      class="fixed bottom-4 rounded-lg border border-border bg-card/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/85"
    >
      <div class="flex flex-col gap-2.5">
        <p class="text-xs leading-relaxed text-foreground">
          Usamos cookies essenciais e, com seu consentimento, de preferências e
          análise. Veja a{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(
                new CustomEvent("legal:open-doc", { detail: "cookies" })
              );
            }}
            class="text-violet-600 underline dark:text-violet-400"
          >
            Política de Cookies
          </a>
          .
        </p>

        {customizing && (
          <div class="space-y-2.5 rounded-md border border-border bg-background/60 p-3 text-xs">
            <div class="flex items-center justify-between gap-3 opacity-70">
              <span>Essenciais (sempre ativos)</span>
              <Switch checked disabled />
            </div>
            <div class="flex items-center justify-between gap-3">
              <span>Preferências</span>
              <Switch checked={preferences} onCheckedChange={setPreferences} />
            </div>
            <div class="flex items-center justify-between gap-3">
              <span>Análise / desempenho</span>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>
          </div>
        )}

        <div class="flex flex-wrap items-center gap-1.5">
          {!customizing ? (
            <button
              type="button"
              onClick={() => setCustomizing(true)}
              class="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-accent"
            >
              Personalizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => save({ essential: true, preferences, analytics })}
              class="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-accent"
            >
              Salvar escolhas
            </button>
          )}
          <button
            type="button"
            onClick={() => save({ essential: true })}
            class="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-accent"
          >
            Só essenciais
          </button>
          <button
            type="button"
            onClick={() =>
              save({ essential: true, preferences: true, analytics: true })
            }
            class="ml-auto rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
          >
            Aceitar todos
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Legal gate (bloqueante)
// ============================================================================

interface PendingDoc {
  kind: "terms" | "privacy";
  version: number;
  title: string;
}
interface DocContent {
  kind: string;
  version: number;
  title: string;
  content: string;
}

// Ordem fixa das etapas do wizard (Termos antes de Privacidade).
const STEP_ORDER: Record<string, number> = { terms: 0, privacy: 1 };

export function LegalGate() {
  // `order` = documentos pendentes na ordem do wizard. O usuário lê → rola →
  // confirma um por vez (sem checkbox). Cada confirmação grava o aceite daquele
  // documento; se fechar no meio, na próxima vez o gate retoma no que falta.
  const [order, setOrder] = useState<PendingDoc[]>([]);
  const [docs, setDocs] = useState<Record<string, DocContent>>({});
  const [step, setStep] = useState(0);
  const [scrolledOK, setScrolledOK] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // GPS capturado uma única vez e reaproveitado entre as etapas (evita 2 prompts).
  const gpsRef = useRef<GpsCoords | null | undefined>(undefined);

  async function load() {
    try {
      const res = await fetch("/api/legal/pending", { credentials: "include" });
      if (!res.ok) return;
      const data = (await res.json()) as {
        pending: PendingDoc[];
        docs: Record<string, DocContent>;
      };
      const sorted = (data.pending ?? [])
        .slice()
        .sort((a, b) => (STEP_ORDER[a.kind] ?? 9) - (STEP_ORDER[b.kind] ?? 9));
      setOrder(sorted);
      setDocs(data.docs ?? {});
      setStep(0);
      setScrolledOK(false);
      gpsRef.current = undefined;
    } catch {
      /* offline/erro — não bloqueia o app */
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Trava o scroll da página de trás enquanto o gate está aberto.
  useEffect(() => {
    if (order.length === 0) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [order.length]);

  // Ao trocar de etapa: volta ao topo e, se o texto não for rolável (curto),
  // libera a confirmação na hora.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    if (el.scrollHeight <= el.clientHeight + 24) setScrolledOK(true);
  }, [step, order]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledOK(true);
    }
  }

  const current = order[step];

  async function confirmStep() {
    if (!scrolledOK || submitting || !current) return;
    setSubmitting(true);
    setError(null);
    if (gpsRef.current === undefined) gpsRef.current = await tryGps();
    try {
      const res = await fetch("/api/legal/accept", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          acceptances: [{ kind: current.kind, version: current.version }],
          coords: gpsRef.current,
          context: "forced_reaccept"
        })
      });
      const data = (await res.json()) as {
        ok?: boolean;
        error?: { message?: string };
      };
      if (!res.ok || !data.ok) {
        if (res.status === 409) {
          setError("Os documentos foram atualizados. Recarregando…");
          setTimeout(() => void load(), 1200);
          return;
        }
        setError(data.error?.message ?? "Não foi possível registrar o aceite.");
        return;
      }
      if (step + 1 < order.length) {
        setStep(step + 1);
        setScrolledOK(false);
      } else {
        setOrder([]); // tudo aceito — fecha o gate
      }
    } catch {
      setError("Falha de conexão. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  if (order.length === 0) return null;
  const doc = current ? docs[current.kind] : null;
  const isLast = step === order.length - 1;

  return (
    <div class="fixed inset-0 z-[10060] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        style={{ maxHeight: "88vh" }}
        class="flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl md:max-w-3xl"
      >
        <header class="border-b border-border px-5 py-3.5">
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-sm font-bold text-foreground">{current?.title}</h2>
            <span class="shrink-0 text-xs text-muted-foreground">
              Etapa {step + 1} de {order.length}
            </span>
          </div>
          {order.length > 1 && (
            <div class="mt-2 flex gap-1.5">
              {order.map((_, i) => (
                <span
                  key={i}
                  class={
                    "h-1 flex-1 rounded-full " +
                    (i <= step ? "bg-violet-500" : "bg-border")
                  }
                />
              ))}
            </div>
          )}
        </header>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          class="min-h-0 flex-1 overflow-y-auto px-5 py-4"
        >
          {doc ? (
            <LegalDocViewer markdown={doc.content} />
          ) : (
            <p class="text-sm text-muted-foreground">Carregando documento…</p>
          )}
        </div>

        <footer class="space-y-2 border-t border-border px-5 py-4">
          {error && <p class="text-xs text-destructive">{error}</p>}
          {!scrolledOK && (
            <p class="text-center text-xs text-muted-foreground">
              Role o documento até o fim para confirmar.
            </p>
          )}
          <button
            type="button"
            disabled={!scrolledOK || submitting}
            onClick={() => void confirmStep()}
            class="w-full rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? "Registrando…"
              : isLast
                ? "Li e confirmo — finalizar"
                : "Li e confirmo — continuar"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ============================================================================
// Visualizador on-demand — abre qualquer documento publicado num modal.
// Escuta o evento global `legal:open-doc` (detail = kind). O <LegalLinks/>
// (em Minha Conta / Guia) e o banner de cookies disparam esse evento.
// Montar <LegalDocModal/> UMA vez globalmente. [[project-legal-consent-system]]
// ============================================================================

const LEGAL_TITLES: Record<string, string> = {
  terms: "Termos de Uso",
  privacy: "Política de Privacidade",
  cookies: "Política de Cookies"
};

export function LegalDocModal() {
  const [kind, setKind] = useState<string | null>(null);
  const [doc, setDoc] = useState<DocContent | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    function onOpen(e: Event) {
      const k = (e as CustomEvent).detail as string;
      if (!k) return;
      setKind(k);
      setDoc(null);
      setLoading(true);
      fetch(`/api/legal/doc?kind=${encodeURIComponent(k)}`, {
        credentials: "include"
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.doc) setDoc(d.doc as DocContent);
        })
        .catch(() => {
          /* ignore */
        })
        .finally(() => setLoading(false));
    }
    window.addEventListener("legal:open-doc", onOpen as EventListener);
    return () =>
      window.removeEventListener("legal:open-doc", onOpen as EventListener);
  }, []);

  // Trava o scroll do fundo enquanto o modal está aberto (senão o scroll
  // interno "vaza" pra página de trás). [[feedback-modal-scroll-lock-and-z]]
  useEffect(() => {
    if (!kind) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [kind]);

  if (!kind) return null;
  const close = () => {
    setKind(null);
    setDoc(null);
  };

  return (
    <div
      style={{ zIndex: 10065 }}
      class="fixed inset-0 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={close}
    >
      <div
        style={{ maxHeight: "85vh" }}
        class="flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl md:max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-5 py-3.5">
          <h2 class="text-sm font-bold text-foreground">
            {doc?.title ?? LEGAL_TITLES[kind] ?? "Documento"}
          </h2>
          <button
            type="button"
            onClick={close}
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Fechar"
          >
            <X class="size-4" />
          </button>
        </header>
        <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p class="text-sm text-muted-foreground">Carregando…</p>
          ) : doc ? (
            <LegalDocViewer markdown={doc.content} />
          ) : (
            <p class="text-sm text-muted-foreground">
              Documento ainda não disponível.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Links pros documentos legais (abrem o <LegalDocModal/> via evento). Usar no
 *  rodapé de Minha Conta, Guia do usuário, etc. */
export function LegalLinks({ class: className }: { class?: string }) {
  const open = (kind: string) =>
    window.dispatchEvent(new CustomEvent("legal:open-doc", { detail: kind }));
  return (
    <div
      class={
        "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground " +
        (className ?? "")
      }
    >
      <button
        type="button"
        onClick={() => open("terms")}
        class="underline underline-offset-2 hover:text-foreground"
      >
        Termos de Uso
      </button>
      <button
        type="button"
        onClick={() => open("privacy")}
        class="underline underline-offset-2 hover:text-foreground"
      >
        Política de Privacidade
      </button>
      <button
        type="button"
        onClick={() => open("cookies")}
        class="underline underline-offset-2 hover:text-foreground"
      >
        Política de Cookies
      </button>
    </div>
  );
}
